/**
 * Side-slide popup shell that wraps <quick-registration>.
 *
 * Public surface (must stay drop-in compatible with the legacy registration popup):
 *
 *   - Listens for global DOM event 'addRegLink' (dispatched by Core.add_signup_link).
 *   - Scans for `.reg_blimp` / `.reg_blimp_a` / `[cr-registration-blimp]` elements at $postLink.
 *   - Reads the same trigger data-attrs the old component read.
 *   - Honors `data-is-opened="1"` / `data-is-signed-in` / `data-registration-event-to-fire` from the widget.
 */
angular.module('quickRegistrationModule').component('quickRegistrationPopup', {
	templateUrl: 'quickRegistrationPopup/quickRegistrationPopup.html',
	bindings: {
		// RPC URLs (forwarded to <quick-registration>)
		magicLinkUrl: '@',          // /rpc/magic-link-request
		quickRegistrationUrl: '@',  // /rpc/quick-registration

		// Page context
		pageId: '@',
		paramSrc: '@',
		paramTier: '@',
		paramKw: '@',
		listingMaskId: '@',
		buildingId: '@',
		historyId: '@',
		articleId: '@',
		detectedUserType: '@',

		// CITY-5336 — registration page-type slug (reporting dimension) forwarded to <quick-registration>.
		registrationPageType: '@',

		// CITY-5336 — server-computed registration version forwarded to <quick-registration>.
		regVersion: '@',

		// Default tokens from the page (overridden if the trigger button supplies its own)
		buildingToken: '@',
		listingToken: '@',
		searchToken: '@',

		// Auto-open
		isOpened: '<',
		isSignedIn: '<',
		closable: '<',

		// CITY-5336 — when isOpened, apply this variant key on auto-open
		// instead of the default 'default'. Used for magic-link failure
		// landing (key 'magic-link-expired').
		autoVariant: '@',

		// Default variant
		regTrigger: '@',
		text: '@',
		altText: '@',

		// Social auth
		googleClientId: '@',
		appleClientId: '@',
		appleRedirectPath: '@',
		appleNonce: '@',
		showSocial: '<',
		oneTapEnabled: '<',

		// Legal
		termsOfUseUrl: '@',
		privacyPolicyUrl: '@',

		// GA tags
		gtagFullRegistration: '@'
	},
	controller: ['$element', '$scope', '$timeout', '$document', '$window', '$cookies', function (
		$element, $scope, $timeout, $document, $window, $cookies) {

		var ctrl = this;

		// ---------- CITY-5446: registration popup position A/B test ----------
		// A = default right-edge slide-in panel; B = full-width sheet that
		// slides up from the bottom. The bucket is sticky per visitor (cookie)
		// so it never flips between page loads, and is reported to GA
		// (dataLayer) so signup conversion is comparable per variant.
		var POS_COOKIE = 'qr_pos_variant';

		ctrl._resolvePositionVariant = function () {
			var v = $cookies.get(POS_COOKIE);
			if (v !== 'A' && v !== 'B') {
				v = Math.random() < 0.5 ? 'A' : 'B';
				var expires = new Date();
				expires.setDate(expires.getDate() + 60);
				$cookies.put(POS_COOKIE, v, { path: '/', expires: expires });
			}
			return v;
		};

		// ---------- variant table — replaces the 7-branch if/else from the old controller ----------

		var DEFAULT_SUBTITLE = 'Sign in or create an account to keep going.';

		var VARIANTS = {
			'default': {
				title: 'Join CityRealty',
				subtitle: 'Unlock New York today! Free access, quick and easy signup.'
			},
			'sign-in': {
				title: 'Welcome back',
				subtitle: 'Enter your email to sign in.'
			},
			'save-listing': {
				title: 'Save this listing',
				subtitle: 'Sign up to save listings and get email alerts on the newest listings.'
			},
			'save-building': {
				title: 'Be the first to know',
				subtitle: 'Sign up to get alerts when new apartments are listed in this building.'
			},
			'acris': {
				title: 'ACRIS Filing Docs',
				subtitle: 'ACRIS links are available to members. Sign up — there’s no obligation.'
			},
			'sign-up-for-newsletter': {
				title: 'Sign up for our newsletter',
				subtitle: 'Get the latest from CityRealty straight to your inbox.'
			},
			'new-listings': {
				title: 'New listings, in your inbox',
				subtitle: 'Sign up to be the first to see listings as they hit the market.'
			},
			'new-dev-deals': {
				title: 'New development deals',
				subtitle: 'Sign up to get alerts on the newest new-development deals.'
			},
			'off-market-deals': {
				title: 'Off-market listings',
				subtitle: 'Sign up to access listings before they go public.'
			},
			// CITY-5336 — landing copy after a stale/invalid magic-link click.
			// The user is bounced here by ParticleTkn::end() with
			// /?reg=link-expired[&next=...]; WRegistrationPopupV2 forwards
			// the variant key via auto-variant.
			'magic-link-expired': {
				title: 'Sign-in link expired',
				subtitle: 'That link is no longer valid. Enter your email below to get a fresh one.'
			},
			// CITY-5339 — confirmation shown AFTER a guest's search was saved
			// (anonymously, in the session). Copy from the ticket mock.
			'search-saved': {
				title: 'Search Saved!',
				subtitle: 'To get instant updates when new listings hit the market, complete the registration below:'
			}
		};

		ctrl._variantKeyFor = function (btn) {
			if (!btn) return 'default';
			var attr = btn.getAttribute ? btn.getAttribute.bind(btn) : function () { return null; };

			if (attr('data-use-save-acris-text') === '1') return 'acris';
			if (attr('data-use-save-building-text') === '1') return 'save-building';
			if (attr('data-use-save-listing-text') === '1') return 'save-listing';

			var callOut = attr('data-call-out');
			if (callOut && VARIANTS[callOut]) return callOut;

			var target = attr('target');
			var classes = (btn.className || '').split(/\s+/);
			// `w_blimp_signin` is the canonical class on stalker / header sign-in buttons.
			// `_signin` and `target="sign-in"` are alternates kept for older callsites.
			if (target === 'sign-in'
				|| classes.indexOf('_signin') !== -1
				|| classes.indexOf('w_blimp_signin') !== -1) {
				return 'sign-in';
			}

			return 'default';
		};

		// ---------- visible state ----------

		ctrl.visible = false;
		ctrl.title = VARIANTS['default'].title;
		ctrl.subtitle = VARIANTS['default'].subtitle;

		// Tokens passed down to <quick-registration>; reset per-open from trigger attrs.
		ctrl.activeBuildingToken = null;
		ctrl.activeListingToken = null;
		ctrl.activeListingAlertOnBuildingToken = null;
		ctrl.activeRegTrigger = null;
		ctrl.activeCalloutBoxType = null;
		ctrl.activeCalloutBoxSourcePage = null;
		ctrl._lastEventToFire = null;

		// ---------- open / close ----------

		ctrl.open = function () {
			ctrl.visible = true;
			$document.find('body').addClass('quick-registration-open');
			if (typeof crGtag !== 'undefined') {
				try { crGtag.track('qr-opened'); } catch (e) { /* swallow */ }
				// CITY-5446 — variant-tagged dataLayer event for GA A/B reporting.
				try {
					crGtag.updateDataLayer({ event: 'qr-opened', qr_popup_position: ctrl.positionVariant });
				} catch (e) { /* swallow */ }
			}
			// Focus trap + Escape-to-close. Reuses Core.disableTabbingOutside
			// from the legacy <registration> popup so behavior is identical.
			// hideFnc is called on Escape key — only when the popup is closable.
			if (typeof Core !== 'undefined' && Core.disableTabbingOutside) {
				ctrl._releaseFocusTrap = Core.disableTabbingOutside(
					$element[0],
					ctrl.closable === false ? null : function () { ctrl.close(); }
				);
			}
			$timeout(function () {
				var firstInput = $element[0].querySelector('input[data-field="email"]');
				if (firstInput) firstInput.focus();
			}, 350);
		};

		ctrl.close = function () {
			if (ctrl.closable === false) return;
			ctrl.visible = false;
			$document.find('body').removeClass('quick-registration-open');
			if (ctrl._releaseFocusTrap) {
				ctrl._releaseFocusTrap();
				ctrl._releaseFocusTrap = null;
			}
			// reset the form so reopening starts fresh
			$timeout(function () {
				var formCtrl = ctrl._getFormController();
				if (formCtrl && typeof formCtrl.reset === 'function') formCtrl.reset();
			}, 300);
		};

		ctrl.onOverlayClick = function (event) {
			// only close if the click was on the overlay itself, not the inner panel
			if (event.target === event.currentTarget) {
				ctrl.close();
			}
		};

		ctrl.onSuccess = function () {
			if (ctrl._lastEventToFire) {
				try {
					var ev = document.createEvent('HTMLEvents');
					ev.initEvent(ctrl._lastEventToFire, true, true);
					document.dispatchEvent(ev);
				} catch (e) { /* swallow */ }
			}
		};

		// ---------- trigger handling ----------

		ctrl._applyVariant = function (btn) {
			var key = ctrl._variantKeyFor(btn);
			var variant = VARIANTS[key] || VARIANTS['default'];
			ctrl.title = variant.title;
			ctrl.subtitle = variant.subtitle;
			ctrl.activeVariant = key;

			var attr = btn && btn.getAttribute ? btn.getAttribute.bind(btn) : function () { return null; };

			ctrl.activeBuildingToken = attr('data-building-token') || ctrl.buildingToken || null;
			ctrl.activeListingToken = attr('data-listing-token') || ctrl.listingToken || null;
			ctrl.activeListingAlertOnBuildingToken = attr('data-listing-alerts-on-buildings-token') || null;

			// Save-to-profile fallback: parse token out of an RPC url
			if (!ctrl.activeListingToken) {
				var rpc = attr('data-rpc-save-to-profile');
				if (rpc) {
					var match = rpc.match(/\?t=([\w\-]+)/);
					if (match && match[1]) ctrl.activeListingToken = match[1];
				}
			}

			ctrl.activeRegTrigger = attr('data-reg-trigger') || ctrl.regTrigger || null;
			ctrl.activeCalloutBoxType = attr('data-call-out') || null;
			ctrl.activeCalloutBoxSourcePage = attr('data-callout-page-type') || null;
			ctrl._lastEventToFire = attr('data-registration-event-to-fire') || null;
		};

		ctrl._handleTriggerClick = function (btn) {
			ctrl._applyVariant(btn);
			ctrl.open();
		};

		// Document-level click delegation: matches every `.reg_blimp`, `.reg_blimp_a`,
		// and `[cr-registration-blimp]` element, including ones inserted later. Avoids
		// jqLite's lack of namespaced-event support and any scan-timing issues.
		// Capture phase so ancestors that stopPropagation can't suppress us.
		ctrl._docClickHandler = function (e) {
			var target = e.target;
			// If the target is a text node, climb to its parent element so .closest works.
			if (target && target.nodeType === 3) target = target.parentElement;
			if (!target || typeof target.closest !== 'function') return;

			var btn = target.closest('.reg_blimp, .reg_blimp_a, [cr-registration-blimp]');
			if (!btn) return;

			e.preventDefault();
			e.stopPropagation();

			$scope.$apply(function () {
				ctrl._handleTriggerClick(btn);
			});
		};

		ctrl._getFormController = function () {
			var formEl = $element[0].querySelector('quick-registration');
			if (!formEl) return null;
			var ctrls = angular.element(formEl).controller('quickRegistration');
			return ctrls || null;
		};

		// CITY-5339 — programmatic open with a named variant, for flows where
		// the popup follows an action instead of gating it (guest save-search):
		//   document.dispatchEvent(new CustomEvent('openQuickRegistrationPopup',
		//       { detail: { variant: 'search-saved' } }));
		// Routes through a synthetic trigger carrying the variant as
		// data-call-out so _variantKeyFor + _applyVariant reuse the real
		// trigger code path (same trick as the auto-open handling below).
		ctrl._openEventHandler = function (e) {
			var variant = e && e.detail && e.detail.variant;
			// $timeout, not $scope.$apply: the event may be dispatched from
			// inside a digest (e.g. crToggleView fires it in a $q .then()),
			// where $apply would throw "$apply already in progress".
			$timeout(function () {
				ctrl._applyVariant({
					getAttribute: function (name) {
						return (name === 'data-call-out' && variant && VARIANTS[variant]) ? variant : null;
					}
				});
				ctrl.open();
			});
		};

		// Defensive: attach a per-element click handler to legacy `.reg_blimp`
		// elements registered via Core.add_signup_link() (which dispatches the
		// 'addRegLink' DOM event). Document delegation already catches the
		// common case — this covers buttons inside ancestors that
		// stopPropagation (MooTools Blimp content, ng-click stop chains).
		// Idempotent via WeakSet: prevents double-binding when
		// Core.add_signup_link() fires repeatedly for the same node, and
		// auto-releases entries when the node is garbage-collected.
		var _attachedNodes = typeof WeakSet === 'function' ? new WeakSet() : null;

		ctrl._addRegLinkHandler = function (e) {
			var raw = e && e.eventEl;
			if (!raw) return;
			// jQuery / MooTools wrappers vs raw nodes. nodeType is the
			// fastest "is this a raw DOM node" check.
			var node = raw && raw.nodeType ? raw : (raw && raw[0]) || null;
			if (!node || !node.addEventListener) return;
			if (_attachedNodes && _attachedNodes.has(node)) return;
			if (_attachedNodes) _attachedNodes.add(node);
			node.addEventListener('click', function (ev) {
				ev.preventDefault();
				ev.stopPropagation();
				$scope.$apply(function () {
					ctrl._handleTriggerClick(node);
				});
			});
		};

		// ---------- lifecycle ----------

		ctrl._dead = false;

		ctrl.$onInit = function () {
			// Move popup to <body> so its fixed positioning escapes any
			// transformed ancestors, and dedupe — we only ever want one mounted.
			// If we lose the dedupe race, $element.remove() and skip $postLink
			// listener registration so we don't leave an orphan document handler
			// pointing at a detached controller.
			var existing = document.body.querySelector('quick-registration-popup');
			if (existing && existing !== $element[0]) {
				$element.remove();
				ctrl._dead = true;
				return;
			}
			if ($element[0].parentElement !== document.body) {
				document.body.appendChild($element[0]);
			}

			// CITY-5446 — decide (and stick) the A/B position bucket. Variant "B"
			// gets the bottom-sheet class via ng-class on #qr-popup-host in the
			// template (the styled element — NOT $element[0], which is the outer
			// <quick-registration-popup> custom tag the LESS doesn't target).
			// Resolved here so it's set before the first open() and stays stable.
			ctrl.positionVariant = ctrl._resolvePositionVariant();
		};

		ctrl.$postLink = function () {
			if (ctrl._dead) return;

			// Capture phase: runs before ancestor click handlers, so even
			// elements wrapped in stopPropagation containers reach us.
			document.addEventListener('click', ctrl._docClickHandler, true);
			document.addEventListener('addRegLink', ctrl._addRegLinkHandler);
			document.addEventListener('openQuickRegistrationPopup', ctrl._openEventHandler);

			// Belt-and-suspenders: scan existing .reg_blimp elements once at
			// startup and attach per-element handlers via the same path the
			// addRegLink listener uses. Catches buttons that loaded before the
			// popup widget mounted and never went through Core.add_signup_link.
			$timeout(function () {
				var nodes = document.querySelectorAll('.reg_blimp, .reg_blimp_a, [cr-registration-blimp]');
				for (var i = 0; i < nodes.length; i++) {
					ctrl._addRegLinkHandler({ eventEl: nodes[i] });
				}
			}, 0);

			// Auto-open if the widget told us to (e.g. unfinished registration return path).
			if (ctrl.isOpened) {
				$timeout(function () {
					// CITY-5336 — apply the named auto-open variant before
					// open() so title/subtitle reflect the landing context
					// (e.g. 'magic-link-expired' after a stale link click).
					// Pass a synthetic "trigger button" carrying the variant
					// as data-call-out so _variantKeyFor + _applyVariant pick
					// it up via the same code path real triggers use.
					if (ctrl.autoVariant && VARIANTS[ctrl.autoVariant]) {
						ctrl._applyVariant({
							getAttribute: function (name) {
								return name === 'data-call-out' ? ctrl.autoVariant : null;
							}
						});
					}
					ctrl.open();
				}, 100);
			}
		};
	}]
});
